
import Vue from "vue";
import { capitalizeFirstLetters } from "@/helpers/generalHelpers";
import {
  agencyMapState,
  agencyMapMutations,
  agencyMapGetters,
  agencyMapActions
} from "@/store/modules/agency";
import { SET_EDIT, SET_EDIT_FIELD } from "@/store/mutation-types";
import { authMapGetters } from "@/store/modules/auth";
import { companiesMapGetters } from "@/store/modules/companies";
import { userMapGetters } from "@/store/modules/user";
import { FormBlock } from "@/components/FormBuilder/types";
import { agencyForm } from "@/forms/admin/agency";
import { omit } from "lodash";
import { convertValuesToLowerCase, objectDifference } from "@/helpers";
import { agencyStatus } from "@/helpers/selectOptions";
import { sameAsPhysicalAddressChangedHandler } from "@/helpers/isSameAsPhysicalAddress";
import { IAgency } from "@/store/modules/agency/types";
import { IUserRestricted } from "@/types/interfaces";
import { getCompanyNames } from "@/helpers/companies";
import { readOnlyAgencyFormFields } from "@/forms/agencyAdmin/readOnlyAgencyFormFields";
export interface IAgencyData {
  errorMessage: string;
  successMessage: string;
  companyIds: any[];
  hasValidCredentials: boolean;
  statusOptions: any[];
  agencyNotFound: boolean;
  validationData: any;
  bulkAction: { disabled: boolean; title: string }[];
  emitOnAction: boolean;
  hideUserListView: boolean;
  topButtonForUserList: string[];
  initialized: boolean;
  tableActionUsers: string[];
  tableActionAgencies: string[];
  currentTableShowing: string;
  changingTables: boolean;
  nextRoute: Function;
  isAdmin: true;
  isLoading: boolean;
  defaultPhoneValue: any;
  hasUpdatedAgencyData: boolean;
  hasAcknowledgedEzlynx: boolean;
  warningString: string;
  fields: FormBlock[];
  restrictedData: IUserRestricted;
  actionType: "restrict" | "desRestrict" | null;
  modalMessage: string;
  modalTitle: string;
  loadingText: string;
}
export default Vue.extend({
  props: {
    agencyIdProp: {
      type: String,
      required: false
    }
  },
  data(): Partial<IAgencyData> {
    return {
      errorMessage: "",
      successMessage: "",
      companyIds: [],
      hasValidCredentials: false,
      statusOptions: agencyStatus,
      agencyNotFound: false,
      validationData: {
        formIsValid: false
      },
      bulkAction: [
        {
          title: "Add Users To Agency",
          disabled: false
        }
      ],
      tableActionUsers: ["Show Sub Agency List"],
      tableActionAgencies: ["Show Agency Members List"],
      emitOnAction: true,
      hideUserListView: false,
      topButtonForUserList: ["Add New User To Agency"],
      initialized: false,
      currentTableShowing: "",
      changingTables: false,
      nextRoute: () => {},
      hasUpdatedAgencyData: false,
      hasAcknowledgedEzlynx: false,
      warningString: ""
    };
  },
  mounted() {
    const { role = "" } = this.$getCurrentUser;
    if (["agencyAdmin", "subAdmin"].includes(role)) {
      this.currentTableShowing = "agency";
    } else {
      this.currentTableShowing = "users";
    }
  },
  methods: {
    ...agencyMapMutations({
      editField: SET_EDIT_FIELD,
      setAgencyData: SET_EDIT
    }),
    ...agencyMapActions(["getAgency"]),
    formFieldBlurHandler({ key, value }: { key: any; value: any }): void {
      if (key === "agencyName") {
        const capitalizedValue = capitalizeFirstLetters(value);
        this.editField({ key, value: capitalizedValue });
      }
      if (key === "agencyCode") {
        this.editField({
          key,
          value: value.toUpperCase()
        });
        if (
          value &&
          value.length >= 3 &&
          !value.toUpperCase().startsWith("AGA-") &&
          !value.toUpperCase().startsWith("CLL-")
        ) {
          this.$modal.show("selectPrefixModal");
        }
      }

      if (key === "companyIds") {
        if (
          !this.hasSelectedInsurorsIndemnity &&
          this.editing.allowInsurorsSeacoast
        ) {
          this.editField({
            key: "allowInsurorsSeacoast",
            value: false
          });
        }
        if (
          !this.hasSelectedInsurorsIndemnitySelect &&
          this.editing.allowInsurorsSelectSeacoast
        ) {
          this.editField({
            key: "allowInsurorsSelectSeacoast",
            value: false
          });
        }
      }
    },
    formFieldChangedHandler({ key, value }: { key: any; value: any }): void {
      const addressKeys = ["mailingAddress", "physicalAddress"];
      if (addressKeys.includes(key)) {
        const { fieldKey, value: fieldValue } = value;
        if (fieldKey.includes("isSameAsPhysicalAddress")) {
          sameAsPhysicalAddressChangedHandler(
            key,
            fieldValue,
            this.editing?.physicalAddress || {},
            this.editField
          );
        }
        this.editField({
          key: `${key}.${fieldKey}` as any,
          value: fieldValue
        });
        if (
          key === "physicalAddress" &&
          this.editing &&
          this.editing.mailingAddress.isSameAsPhysicalAddress
        ) {
          this.editField({
            key: `mailingAddress.${fieldKey}` as any,
            value: fieldValue
          });
        }
      } else if (
        key === "allowEzlynxRating" &&
        value &&
        !this.hasAcknowledgedEzlynx
      ) {
        this.warningString = `By accepting this option, you acknowledge that ${
          this.editing?.agencyCode ? this.editing.agencyCode : "agency"
        }'s' data on this platform may be shared with Ezlynx.`;
        this.$modal.show("acknowledgeDataSharing");
      } else {
        this.hasAcknowledgedEzlynx = false;
        this.editField({ key, value });
      }
    },
    prefixSelected(value: string): void {
      if (value && value !== "NONE") {
        this.editField({
          key: "agencyCode",
          value: `${value}-${this.editing.agencyCode.toUpperCase()}`
        });
      }
      this.$modal.hide("selectPrefixModal");
    },
    acceptEzlynxAcknowledgement(): void {
      this.$modal.hide("acknowledgeDataSharing");
      this.hasAcknowledgedEzlynx = true;
      this.editField({
        key: "allowEzlynxRating",
        value: true
      });
    },
    declineEzlynxAcknowledgement(): void {
      this.$modal.hide("acknowledgeDataSharing");
      this.hasAcknowledgedEzlynx = false;
      this.editField({
        key: "allowEzlynxRating",
        value: false
      });
    },
    hasSelectedSpecificCompany(companyNumber: number): boolean {
      const companyIds = this.editing?.companyIds;

      const foundCompany = this.companies.find(
        company => company.companyNumber === companyNumber
      );

      return (
        companyIds &&
        companyIds.length > 0 &&
        companyIds.includes(foundCompany?._id)
      );
    },
    agencyFields(agencyIsDeleted?: boolean): FormBlock[] {
      const { agencyAdminData = null, subAdminData = null } =
        this.editing || {};
      const hasAgencyAdminInfo =
        Boolean(agencyAdminData) || Boolean(subAdminData);

      return agencyForm({
        userIsAdmin: this.isAdmin,
        userIsUnderwriter: this.$isCurrentUserUnderwriter,
        agencyHasAdmin: hasAgencyAdminInfo,
        agencyIsDeleted: agencyIsDeleted,
        hasSelectedInsurorsIndemnitySelect: this
          .hasSelectedInsurorsIndemnitySelect,
        hasSelectedInsurorsIndemnity: this.hasSelectedInsurorsIndemnity,
        agencyHasUnderwriter: this.agencyHasUnderwriter
      });
    }
  },
  computed: {
    ...agencyMapGetters(["getEditingAgency", "getAgencyById"]),
    ...authMapGetters(["getCurrentUser"]),
    ...agencyMapState(["editing", "makingApiRequest"]),
    ...companiesMapGetters(["companies"]),
    ...userMapGetters(["getUnderWritersFromStore"]),
    editingFormatted(): any {
      return this.editing;
    },
    agencyHasUnderwriter(): boolean {
      const { underWriterData = {} } = this.editing || {};
      return Object.keys(underWriterData).length > 0;
    },
    companyOptions(): { label: string; value: string }[] {
      const initData = this.$store.state.auth["initData"];
      const { companies = [] } = initData;
      return companies
        .filter((company: any) => company.companyName && company._id)
        .map((company: any) => ({
          label: `${
            company.companyNumber == 99 ? 20 : company.companyNumber
          } - ${company.companyName}`,
          value: company._id,
          companyName: company.companyName
        }))
        .sort(function(
          str1: { companyName: string },
          str2: { companyName: string }
        ) {
          return str1.companyName.localeCompare(str2.companyName);
        });
    },
    agency(): IAgency | undefined {
      return this.getAgencyById(this.agencyId);
    },
    updatedFields(): any {
      const edited = this.getEditingAgency;
      const original = this.agency;
      const allowEmptyFields = [
        "mailingAddress.streetType",
        "physicalAddress.streetType"
      ];
      return edited && original
        ? omit(objectDifference(edited, original, allowEmptyFields), [
            "underWriterData"
          ])
        : {};
    },
    isAdminOrUnderwriter(): boolean {
      return ["admin", "underwriting"].includes(
        this.getCurrentUser?.role || ""
      );
    },
    convertedUpdatedFields(): boolean {
      let editingAgency = this.getEditingAgency;
      let agency = this.agency;

      if (!this.isAdminOrUnderwriter) {
        editingAgency = omit(editingAgency, [
          "allowInsurorsSeacoast",
          "allowInsurorsSelectSeacoast"
        ]);
        agency = omit(agency, [
          "allowInsurorsSeacoast",
          "allowInsurorsSelectSeacoast"
        ]);
      }
      const convertedEditedAgency =
        convertValuesToLowerCase(editingAgency as IAgency) || {};
      const convertedOriginalAgency =
        convertValuesToLowerCase(agency as IAgency) || {};

      const toIgnore = [
        "subAgencyOf",
        "physicalAddress.unitNumber",
        "mailingAddress.unitNumber",
        "mailingAddress.streetType",
        "physicalAddress.streetType",
        "mailingAddress.streetDirection",
        "physicalAddress.streetDirection"
      ];
      const convertedDifference = objectDifference(
        convertedEditedAgency,
        convertedOriginalAgency,
        toIgnore
      );
      return !!Object.values(convertedDifference).length;
    },
    disablePrimaryToolbar(): boolean {
      return !this.convertedUpdatedFields || !this.formIsValid;
    },
    agencyId(): any {
      return this.$route.params.agencyId || this.agencyIdProp;
    },
    agencyCompanyNames(): any {
      return getCompanyNames(
        this.companyOptions,
        this?.editing?.companyIds
      ).map(company => {
        return { companyName: company };
      });
    },

    formIsValid(): boolean {
      return Boolean(
        this.validationData.formIsValid &&
          this.editing.companyIds.length &&
          this.editing.status
      );
    },
    readOnlyFormFields(): FormBlock[] {
      return readOnlyAgencyFormFields(this.agencyHasUnderwriter);
    },
    isAdmin(): boolean {
      return this.getCurrentUser?.role === "admin";
    },
    isOnboarding(): boolean {
      return (
        this.getCurrentUser?.status === "pendingTutorial" ||
        this.getCurrentUser?.status === "completedTutorial"
      );
    },
    hasSelectedInsurorsIndemnitySelect(): boolean {
      return this.hasSelectedSpecificCompany(32);
    },

    hasSelectedInsurorsIndemnity(): boolean {
      return this.hasSelectedSpecificCompany(26);
    },
    queryOverride(): any {
      return {
        "data.agencyId": this.editing?._id,
        activityType: "AgencyActivity"
      };
    }
  }
});
